/**
 * The environment @signifi/vision-shell expects from its host.
 *
 * The shell imports no http client, store or router of its own; this is where
 * vision-central's are adapted to it. The root component provides it once, above
 * both the shell layout and the popup sign-in landing, so the two share it.
 *
 * - `http` is the app's axios, so the shell's calls ride the session interceptor
 *   the router installs on it.
 * - The session and the portal selection stay in the Vuex store: the router guard
 *   validates the session before the shell mounts, and the dashboards read the
 *   portal from `filters/portal` whichever application renders them.
 * - A path this router matches is a router push; any other path belongs to another
 *   application and is a full navigation to it.
 */
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'
import { provideShellEnv, createNavigate, THEME_STORAGE_KEY } from '@signifi/vision-shell'
import store from '@/store'
import { APPS, MENUS } from '@/config/navigation'
import { validatePin } from '@/utils/pin-validator'

/**
 * @param {{ router: { push: Function, resolve: Function }, route: { path: string, query: object } }} host
 *   the app's router, and its current route as the reactive object `useRoute()` returns.
 */
export function visionCentralShellEnv({ router, route }) {
  return {
    http: axios,
    navigation: { apps: APPS, menus: MENUS },
    session: () => store.getters['auth/session'],
    setSession: (session) => store.commit('auth/setSession', session),
    portal: {
      get: () => store.getters['filters/portal'],
      set: (portals) => store.dispatch('filters/setPortal', portals),
      clear: () => store.dispatch('filters/clearPortal'),
    },
    currentRoute: () => route,
    navigate: createNavigate({
      push: (path) => router.push(path),
      owns: (path) => router.resolve(path).matched.length > 0,
    }),
    validatePin,
    themeStorageKey: THEME_STORAGE_KEY,
  }
}

export function useShellHost() {
  provideShellEnv(visionCentralShellEnv({ router: useRouter(), route: useRoute() }))
}
